import * as React from 'react'
import { clsx } from 'clsx'
import { DiffItem, getStatusFromDiffItem } from '@basehub/utils/json-diff/index'
import DiffMatchPatch from 'diff-match-patch'
import {
  FloatingPortal,
  useFloating,
  flip,
  shift,
  inline,
  autoUpdate,
  offset,
  useHover,
  useInteractions,
} from '@floating-ui/react'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'

import {
  type BlockChange,
  type RichTextBlock,
  type Block,
  DocumentBlock,
  isRichTextBlock,
  isTextBlock,
  TextBlock,
  AgentBlock,
} from '@basehub/data/replicache/index'

import './rt-editor.scss'
import {
  commentExtensionsForTransformations,
  plainTextToYDocBase64State,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { AtLeast, TreeManager } from '@basehub/utils/tree/index'
import { invariant } from '@epic-web/invariant'
import { JSONContent } from '@tiptap/core'
import { getDiffResultsWithNormalizedIndexes } from '@basehub/data/replicache/mutators/repo-space/commit/merge-helpers'
import { useStorage } from '~/hooks/use-storage'
import { sortObjectKeys } from '@basehub/utils/object'
import { hash } from '@basehub/utils/hash'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useSelectedBranch } from '~/lib/commits/use-selected-branch-or-commit'
import { NestedDocumentBlockLink } from '../document/nested/link'
import { useGetTabPrefix } from '../../tabs/store'
import { useRepoWorkers } from '~/workers/provider'
import { useQuery } from '@tanstack/react-query'
import { run } from '@basehub/utils'

const dmp = new DiffMatchPatch()

type RichTextBlockInputProps = {
  status: BlockChange['status']
  current: AtLeast<
    RichTextBlock | AgentBlock | TextBlock,
    'id' | 'value' | 'type'
  > | null
  previous: AtLeast<
    RichTextBlock | AgentBlock | TextBlock,
    'id' | 'value' | 'type'
  > | null
  conflicting: RichTextBlock | AgentBlock | null
  condition: 'added' | 'removed'
  changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
  className?: string
  change: AtLeast<BlockChange, 'id' | 'status'>
}

export const RichTextBlockDiff: React.FC<RichTextBlockInputProps> = ({
  current,
  previous,
  condition,
  className = '',
  change,
  changesTree,
  ...rest
}) => {
  const [currentJson, setCurrentJson] = React.useState<JSONContent | undefined>(
    undefined
  )
  const [previousJson, setPreviousJson] = React.useState<
    JSONContent | undefined
  >(undefined)

  React.useEffect(() => {
    run(async () => {
      if (current?.value) {
        setCurrentJson(
          stateToJson(
            current.type === 'rich-text' || current.type === 'agent'
              ? current.value.state
              : (
                  await plainTextToYDocBase64State(
                    current.value,
                    commentExtensionsForTransformations
                  )
                ).base64
          ) ?? {}
        )
      }
    })
  }, [current])

  React.useEffect(() => {
    if (!previous) return
    if (previous.type === 'text') {
      setPreviousJson({
        content: [
          {
            type: 'doc',
            content: [{ type: 'text', text: previous.value ?? undefined }],
          } satisfies JSONContent,
        ],
      })
      return
    }

    setPreviousJson(stateToJson(previous.value.state))
  }, [previous])

  const { diffWorker } = useRepoWorkers()

  const { data: diffData } = useQuery(
    [
      `rich-text-diff-${current?.id}-${previous?.id}-${current?.updatedAt}-${previous?.updatedAt}`,
    ],
    async () => {
      if (!diffWorker) throw new Error('not enabled')
      return await diffWorker.getDiffBetweenRichTexts(
        `rich-text-diff-${current?.id}-${previous?.id}-${current?.updatedAt}-${previous?.updatedAt}`,
        { a: previousJson?.content || [], b: currentJson?.content || [] }
      )
    },
    { enabled: !!diffWorker && !!previousJson && !!currentJson }
  )

  const hiddenSegmentsInitialValue = React.useMemo(() => {
    if (diffData?.diffResult === undefined) return undefined
    const segments: Array<{ trail: number[]; hidden: boolean }> = []
    diffData.diffResult.forEach((item, i) => {
      const status = getStatusFromDiffItem(item)
      const currentSegment = segments[segments.length - 1]
      if (status === 'unmodified') {
        if (currentSegment) {
          currentSegment.trail.push(i)
        } else {
          segments.push({ trail: [i], hidden: true })
        }
      } else {
        if (currentSegment) {
          segments.push({ trail: [], hidden: true })
        }
      }
    })
    return segments
  }, [diffData?.diffResult])

  const [hiddenSegments, setHiddenSegments] = useStorage(
    'sessionStorage',
    (z) =>
      z
        .object({
          trail: z.number().array(),
          hidden: z.boolean(),
        })
        .array(),
    `hidden-segments-${current?.id}-${previous?.id}`,
    {
      initialValue: hiddenSegmentsInitialValue ?? [],
      enabled: !!hiddenSegmentsInitialValue,
    }
  )

  /**
   * Show rich text diff. `previous` can be the common ancestor, or the current head commit. `conflicting` comes if there's a conflict somewhere.
   *
   * Some principles:
   * 1. keep height by using shadow elements
   * 2. left shows deletions, right shows additions.
   * 3. both panels show their FULL content (highlighting deletions/additions respectively)
   * 4. we use diff-match-patch to diff text
   * 5. for attributes, we show ON HOVER the diff (so, don't pass to markdown)
   *
   * How?
   * 1. Start from the json diff
   * 2. For each diff item, we get the node + the matching node
   * 2.1 this logic is quite interesting. if condition === 'removed' (left pane), insertions won't be here. so we render an empty node. if condition === 'added', deletions won't be here, so we render an empty node.
   * 2.2 we'll get the matchingNode and render it as well, although we make it invisible, and so we sync both pane's heights.
   * 2.3 reorders are special. in the left pane, we'll highlight the full node in gray (reorder color). we'll render the shadow (match node) in the target reorder index (hidde of course!). in the right pane, we'll render the shadow in the origin (hidden of course) and the target in the target...
   * 3. we know where the conflicts are. the index in the common json at least.
   * 3.1 ... ...
   */

  return (
    <>
      <div
        key={`${current?.id}-${previous?.id}-${current?.updatedAt}-${previous?.updatedAt}`}
        className={clsx(
          'rt-editor blocked overflow-hidden relative !min-h-[unset]',
          'max-w-none h-full flex flex-col',
          className,
          '[--p]:[12px,0px] [--px]:[12px]'
        )}
      >
        <div className="ProseMirror !px-0 h-full">
          {diffData?.diffResult.map((diff, i) => {
            const indexInPreviousNodes =
              diffData.diffResultWithNormalizedIndexesToPreviousNodes.findIndex(
                (item) => item._index === diff._index
              )
            const indexInCurrentNodes =
              diffData.diffResultWithNormalizedIndexesToCurrentNodes.findIndex(
                (item) => item._index === diff._index
              )

            const previousNode = previousJson?.content?.[indexInPreviousNodes]
            const currentNode = currentJson?.content?.[indexInCurrentNodes]
            let conflictingNode: JSONContent | undefined = undefined

            const resolvedIndex = Math.max(
              indexInCurrentNodes,
              indexInPreviousNodes
            )

            if (change.status === 'conflict') {
              const conflictIndex =
                change.conflictPaths?.findIndex(
                  (path) => path === `value.state.${indexInPreviousNodes}`
                ) ?? -1
              if (conflictIndex !== -1) {
                conflictingNode = change.conflictValues?.[conflictIndex]
                  ?.head as undefined | JSONContent
              }
            }

            const hiddenSegmentIndex = hiddenSegments.findIndex((segment) =>
              segment.trail.includes(i)
            )

            if (hiddenSegmentIndex !== -1) {
              const hiddenSegment = hiddenSegments[hiddenSegmentIndex]!
              if (hiddenSegment.hidden) {
                if (hiddenSegment.trail[0] === i) {
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setHiddenSegments((prev) => {
                          const newSegments = [...(prev ?? [])].map((s, i) =>
                            i === hiddenSegmentIndex
                              ? { ...s, hidden: false }
                              : s
                          )
                          return newSegments
                        })
                      }}
                      className={clsx(
                        'my-2 min-h-[32px] flex items-center justify-center bg-dark-shade-hover cursor-default border-dark-elevated text-xs text-dark-muted hover:text-dark-dim border-t border-b border-dashed py-2 w-full'
                      )}
                    >
                      Show {hiddenSegment.trail.length} unmodified{' '}
                      {hiddenSegment.trail.length === 1 ? 'node' : 'nodes'}
                    </button>
                  )
                }
                return null
              }
            }
            return (
              <NodeDiff
                key={i}
                blockId={change?.id}
                diff={diff}
                condition={condition}
                previousNode={previousNode}
                currentNode={currentNode}
                conflictingNode={conflictingNode}
                change={change}
                // debug={currentNode?.type === 'bulletList'}
                changesTree={changesTree}
                resolvedIndex={resolvedIndex}
              />
            )
          })}
        </div>
      </div>
      <OverridesDiff
        condition={condition}
        className={className}
        change={change}
        changesTree={changesTree}
        {...rest}
      />
    </>
  )
}

const NodeDiff = ({
  blockId,
  diff,
  condition,
  currentNode,
  previousNode,
  conflictingNode,
  change,
  debug,
  changesTree,
  resolvedIndex,
}: {
  blockId: string
  diff: DiffItem
  condition: RichTextBlockInputProps['condition']
  currentNode: JSONContent | undefined
  previousNode: JSONContent | undefined
  conflictingNode: JSONContent | undefined
  change: AtLeast<BlockChange, 'id'>
  debug?: boolean
  changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  resolvedIndex: number
}) => {
  const diffStatus = getStatusFromDiffItem(diff)
  const [height, setHeight] = React.useState<string>('auto')
  const containerRef = React.useRef<HTMLDivElement>(null)

  const node =
    condition === 'removed' ? (conflictingNode ?? previousNode) : currentNode
  const nodeMatch =
    condition === 'added' ? (conflictingNode ?? previousNode) : currentNode

  const classes = ['my-8']

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const matchEl = document.querySelector<HTMLElement>(
      `[data-diff-block-id="${blockId}"][data-diff-condition="${
        condition === 'added' ? 'removed' : 'added'
      }"][data-diff-index="${diff._index}"]`
    )
    if (!matchEl) return

    const observer = new ResizeObserver(() => {
      const elHeight = el.offsetHeight
      const matchElHeight = matchEl.offsetHeight
      if (matchElHeight > elHeight) {
        setHeight(`${matchElHeight}px`)
      }
    })

    observer.observe(matchEl)
    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [condition, diff._index, blockId])

  return (
    <div
      className={clsx(classes)}
      style={{ height }}
      ref={containerRef}
      data-diff-condition={condition}
      data-diff-index={diff._index}
      data-diff-block-id={blockId}
    >
      <StatusWrapper
        status={conflictingNode ? 'conflict' : diffStatus}
        change={change}
        condition={condition}
        index={resolvedIndex}
      >
        {node && (
          <Node
            node={node}
            nodeMatch={nodeMatch}
            diff={diff}
            condition={condition}
            isConflict={!!conflictingNode}
            rootNodeStatus={diffStatus}
            debug={debug}
            changesTree={changesTree}
          />
        )}
      </StatusWrapper>
    </div>
  )
}

const StatusWrapper = ({
  children,
  status,
  condition,
  change,
  index,
}: {
  children: React.ReactNode
  status:
    | 'conflict'
    | 'added'
    | 'removed'
    | 'modified'
    | 'unmodified'
    | 'reordered'
  condition: RichTextBlockInputProps['condition']
  change: AtLeast<BlockChange, 'id'>
  index: number
}) => {
  const { cr } = useSelectedBranch()
  const replicache = useRepoReplicache().data?.replicache

  const conflictKey = `${change.id}/${index}`
  const conflictResolution = cr?.conflictResolutions?.[conflictKey]
  const isSelected = conflictResolution
    ? condition === 'removed'
      ? conflictResolution === 'base'
      : conflictResolution === 'source'
    : false

  const resolveConflict = async () => {
    if (!cr) return
    await replicache?.mutate.resolveConflict({
      type: 'block',
      contentRequestId: cr.id,
      sourceBlockId: change?.id,
      richTextNodeIndex: index,
      resolution: condition === 'removed' ? 'base' : 'source',
    })
  }

  const keepUnresolved = async () => {
    if (!cr) return
    await replicache?.mutate.resolveConflict({
      type: 'block',
      contentRequestId: cr.id,
      sourceBlockId: change?.id,
      richTextNodeIndex: index,
      resolution: null,
    })
  }

  if (!children && status !== 'conflict') {
    return <div className="p-2 italic text-xs text-dark-muted">Empty</div>
  }
  if (status === 'unmodified' || status === 'modified') {
    return <div className="px-2">{children}</div>
  }
  return (
    <div
      className={clsx(
        'border-t border-b border-dashed',
        status === 'conflict' && '!border-none',
        status === 'added' && 'border-green-diff bg-green-diff/5',
        status === 'removed' && 'border-red-diff bg-red-diff/5',
        index === 0 && '!border-t-0'
      )}
    >
      {status === 'conflict' && (
        <button
          onClick={isSelected ? keepUnresolved : resolveConflict}
          className={clsx(
            'cursor-default border-y border-dashed w-full py-0.5 px-2 text-xs text-left',
            isSelected
              ? 'border-green-diff bg-green-diff/5 text-green-diff'
              : 'border-yellow-diff bg-yellow-diff/5 text-yellow-diff',
            index === 0 && '!border-t-0'
          )}
        >
          {isSelected && <span>✓</span>} Select{isSelected && 'ed'}{' '}
          {condition === 'removed' ? 'base' : 'current'} version
        </button>
      )}
      <div className="px-2 py-3">
        {children || (
          <div className="p-2 italic text-xs text-dark-muted">Empty</div>
        )}
      </div>
    </div>
  )
}

const Node = React.memo(
  ({
    node,
    nodeMatch,
    diff,
    condition,
    isConflict,
    rootNodeStatus,
    debug,
    changesTree,
  }: {
    node: JSONContent
    nodeMatch: JSONContent | undefined
    diff: DiffItem | undefined
    condition: RichTextBlockInputProps['condition']
    isConflict: boolean
    rootNodeStatus: ReturnType<typeof getStatusFromDiffItem>
    debug?: boolean
    changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  }) => {
    const diffContent = (diff?.[1]?.content ?? []) as DiffItem[]
    const {
      diffResultWithNormalizedIndexesToPreviousNodes,
      diffResultWithNormalizedIndexesToCurrentNodes,
    } = getDiffResultsWithNormalizedIndexes(diffContent)
    const diffStatus = getStatusFromDiffItem(diff)
    const tabPrefix = useGetTabPrefix()

    let Handler = ({
      children,
      className,
    }: {
      children?: React.ReactNode
      className?: string
    }) => <span className={className}>{children}</span>
    let shouldRecursivelyRenderChildren = false
    switch (node?.type) {
      case 'text': {
        Handler = () => (
          <TextList
            content={node.content ?? []}
            contentMatch={nodeMatch?.content ?? []}
            condition={condition}
            diffStatus={diffStatus}
            isConflict={isConflict}
            rootNodeStatus={rootNodeStatus}
          />
        )
        break
      }
      case 'paragraph': {
        Handler = () => {
          return (
            <p className="rt-paragraph">
              <TextList
                content={node.content ?? []}
                contentMatch={nodeMatch?.content ?? []}
                condition={condition}
                diffStatus={diffStatus}
                isConflict={isConflict}
                rootNodeStatus={rootNodeStatus}
              />
            </p>
          )
        }
        break
      }
      case 'heading': {
        const level = (node.attrs?.level ?? 1) as 1 | 2 | 3 | 4 | 5 | 6
        // const levelChanged = nodeMatch?.attrs?.level !== level
        const Component = `h${level}` as const
        Handler = () => (
          <Component className="rt-heading">
            <TextList
              content={node.content ?? []}
              contentMatch={nodeMatch?.content ?? []}
              condition={condition}
              diffStatus={diffStatus}
              isConflict={isConflict}
              rootNodeStatus={rootNodeStatus}
            />
          </Component>
        )
        break
      }
      case 'image': {
        Handler = () => (
          <div className="rt-image">
            <figure>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={node.attrs?.src} alt="" />
              <figcaption>{node.attrs?.caption}</figcaption>
            </figure>
          </div>
        )
        break
      }
      case 'video': {
        Handler = () => (
          <figure className="rt-video">
            <video
              className="w-full"
              src={node.attrs?.src}
              controls
              style={{ aspectRatio: node.attrs?.aspectRatio }}
            />
          </figure>
        )
        break
      }
      case 'table': {
        // just show the original table and the new table as a whole thing
        shouldRecursivelyRenderChildren = true
        // calc table width in base of tiptap content
        const firstRow = node.content?.[0]
        const tableWidth = firstRow?.content?.reduce((acc, child) => {
          if (child.type === 'tableCell' || child.type === 'tableHeader') {
            const width = child.attrs?.colwidth?.[0] ?? 200
            return acc + width
          }
          return acc
        }, 0)
        Handler = ({ children, className }) => (
          <div className="tableWrapper rt-table">
            <table
              className={className || undefined}
              style={{
                width: tableWidth ? tableWidth + 'px' : 'auto',
              }}
            >
              {children}
            </table>
          </div>
        )
        break
      }
      case 'tableRow': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <tr className={clsx(className, 'rt-table-row')}>{children}</tr>
        )
        break
      }
      case 'tableHeader': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <th
            style={{
              width: node.attrs?.colwidth?.[0] ?? 200,
            }}
            colSpan={node.attrs?.colspan}
            className={clsx(className, 'rt-table-header')}
          >
            <div className="rt-table-header-wrapper">{children}</div>
          </th>
        )
        break
      }
      case 'tableCell': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <td
            className={clsx(className, 'rt-table-cell')}
            style={{
              width: node.attrs?.colwidth?.[0] ?? 200,
            }}
          >
            <div className="rt-table-cell-wrapper">{children}</div>
          </td>
        )
        break
      }
      case 'taskList': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <ul className={clsx(className, 'rt-task-list')} data-type="taskList">
            {children}
          </ul>
        )
        break
      }
      case 'orderedList': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <ol className={clsx(className, 'rt-ordered-list')}>{children}</ol>
        )
        break
      }
      case 'bulletList': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <ul className={clsx(className, 'rt-bullet-list')}>{children}</ul>
        )
        break
      }
      case 'listItem': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <li className={clsx(className, 'rt-list-item')}>{children}</li>
        )
        break
      }
      case 'taskItem': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children }) => (
          <li
            className="flex items-baseline rt-task-item"
            data-checked={node.attrs?.checked}
          >
            <label>
              <input type="checkbox" checked={node.attrs?.checked} readOnly />
            </label>
            {children}
          </li>
        )
        break
      }
      case 'codeBlock': {
        const language = node.attrs?.language || 'Plain text'
        const languageMatch = nodeMatch?.attrs?.language || 'Plain text'

        Handler = ({ className }) => (
          <div className="pt-2 rt-codeblock">
            <div className="text-xs text-dark-muted flex justify-end">
              <InlineTextDiff
                text={language}
                textMatch={languageMatch}
                condition={condition}
                invertBasedOnCondition
              />
            </div>
            <pre className={clsx(className, '!mt-2')}>
              <code>
                <TextList
                  content={node.content ?? []}
                  contentMatch={nodeMatch?.content ?? []}
                  condition={condition}
                  diffStatus={diffStatus}
                  isConflict={isConflict}
                  rootNodeStatus={rootNodeStatus}
                />
              </code>
            </pre>
          </div>
        )
        break
      }
      case 'blockquote': {
        shouldRecursivelyRenderChildren = true
        Handler = ({ children, className }) => (
          <blockquote className={clsx(className, 'rt-blockquote')}>
            {children}
          </blockquote>
        )
        break
      }
      case 'basehub-block': {
        Handler = () => {
          const change = changesTree?.getNode(node.attrs?.id)
          const block =
            condition === 'added'
              ? change?.value.current
              : change?.value.previous

          if (!block) return <></>
          return (
            <NestedDocumentBlockLink
              mode="readonly"
              prefix={tabPrefix}
              block={block as DocumentBlock}
              parentBlock={null}
            />
          )
        }
        break
      }
      default:
        shouldRecursivelyRenderChildren = true
        return (
          <pre>
            <code>{JSON.stringify({ diff, type: node.type }, null, 2)}</code>
          </pre>
        )
    }

    let children = <></>
    if (shouldRecursivelyRenderChildren) {
      children = (
        <>
          {node.content?.map((nextNode, i) => {
            const itemDiff =
              condition === 'removed'
                ? diffResultWithNormalizedIndexesToPreviousNodes[i]
                : diffResultWithNormalizedIndexesToCurrentNodes[i]
            const matchIndex = (
              condition === 'removed'
                ? diffResultWithNormalizedIndexesToCurrentNodes
                : diffResultWithNormalizedIndexesToPreviousNodes
            ).findIndex((item) => item._index === itemDiff?._index)

            const nextNodeMatch = nodeMatch?.content?.[matchIndex]

            return (
              <Node
                key={i}
                node={nextNode}
                nodeMatch={nextNodeMatch}
                diff={itemDiff}
                condition={condition}
                isConflict={isConflict}
                rootNodeStatus={rootNodeStatus}
                debug={debug}
                changesTree={changesTree}
              />
            )
          })}
        </>
      )
    }

    return (
      <Handler
        className={clsx(
          rootNodeStatus !== 'added' &&
            diffStatus === 'added' &&
            '!bg-green-diff/30',
          rootNodeStatus !== 'removed' &&
            diffStatus === 'removed' &&
            '!bg-red-diff/30'
        )}
      >
        {children}
      </Handler>
    )
  }
)

type Mark = {
  attrs: Record<string, unknown>
  type: string
}

const TextList = React.memo(
  ({
    content,
    contentMatch,
    condition,
    diffStatus,
    isConflict,
    rootNodeStatus,
  }: {
    content: JSONContent[]
    contentMatch: JSONContent[]
    condition: RichTextBlockInputProps['condition']
    diffStatus: ReturnType<typeof getStatusFromDiffItem>
    isConflict: boolean
    rootNodeStatus: ReturnType<typeof getStatusFromDiffItem>
  }) => {
    const spanId = 'span_' + React.useId()

    /**
     * For attributes and marks, we'll include them in the string as a unicode character.
     * Each mark combination will be sorted and mapped to a unique unicode.
     * Then it'll be diffed, and then it'll be converted back to the original string (with the marks applied).
     *
     * https://github.com/google/diff-match-patch/wiki/Plain-Text-vs.-Structured-Content#doing-it-anyway
     */
    const UNICODE_START = 0xe000 // pua init
    let nextUnicode = UNICODE_START

    const marksHashMap = new Map<string, Mark[]>()
    const marksHashToUnicodeMap = new Map<string, string>()
    const marksUnicodeToHashMap = new Map<string, string>()

    const marksToUnicode = ({ marks }: { marks: Mark[] | undefined }) => {
      let hasMarks = false
      const sorted = [...(marks ?? [])]
        .sort((a, b) => {
          return a.type.localeCompare(b.type)
        })
        .map((m) => {
          hasMarks = true
          return { ...m, attrs: sortObjectKeys(m.attrs ?? {}) }
        })

      if (!hasMarks) {
        return { hasMarks }
      }

      const marksHash = hash(JSON.stringify(marks))
      marksHashMap.set(marksHash, sorted)
      let unicode = marksHashToUnicodeMap.get(marksHash)

      if (!unicode) {
        unicode = String.fromCodePoint(nextUnicode++)
        marksHashToUnicodeMap.set(marksHash, unicode)
        marksUnicodeToHashMap.set(unicode, marksHash)
      }

      return { hasMarks, unicode, marksHash }
    }

    const text =
      content
        ?.map((n) => {
          const text = n.text ?? ''
          if (!text) return ''

          const { hasMarks, unicode } = marksToUnicode({
            marks: n.marks as Mark[] | undefined,
          })

          if (!hasMarks) return text
          return `${unicode}${text}${unicode}`
        })
        .join('') ?? ''
    const textMatch =
      contentMatch
        ?.map((n) => {
          const text = n.text ?? ''
          if (!text) return ''

          const { hasMarks, unicode } = marksToUnicode({
            marks: n.marks as Mark[] | undefined,
          })

          if (!hasMarks) return text
          return `${unicode}${text}${unicode}`
        })
        .join('') ?? ''

    const diffs = diffText({
      text,
      textMatch,
      condition,
      invertBasedOnCondition: true,
    })

    const openMarksSet = new Set<string>()

    const getHtmlFromTextWithUnicodeMarks = ({ text }: { text: string }) => {
      const splitText = text.split('')
      const htmx = splitText.map((char) => {
        const markHash = marksUnicodeToHashMap.get(char)
        if (markHash) {
          const isClosingMark = openMarksSet.has(markHash)
          if (!isClosingMark) {
            openMarksSet.add(markHash)
          } else {
            openMarksSet.delete(markHash)
          }

          const marks = marksHashMap.get(markHash)
          invariant(marks, 'marks not found')

          const children = { opening: '', closing: '' }
          const classes: string[] = []
          marks.forEach((mark) => {
            switch (mark.type) {
              case 'bold':
                classes.push('font-bold rt-bold')
                break
              case 'italic':
                classes.push('italic rt-italic')
                break
              case 'link':
                children.opening = `<a class="rt-link" href="${
                  mark.attrs.href
                }" data-open-in-new-tab="${
                  String(mark.attrs.target).toLowerCase() === '_blank'
                }">`
                children.closing = '</a>'
                break
              case 'basehub-inline-block':
                classes.push('rt-basehub-inline-block')
                children.opening = `<span id="${mark.attrs.id}" data-inline-block="true">`
                children.closing = '</span>'
                break
              case 'code': {
                children.opening = `<code class="rt-code">`
                children.closing = '</code>'
                break
              }
              case 'strike': {
                children.opening = `<s class="rt-strike">`
                children.closing = '</s>'
                break
              }
              case 'kbd': {
                children.opening = `<kbd class="rt-kbd">`
                children.closing = '</kbd>'
                break
              }
              default:
                break
            }
          })

          if (isClosingMark) return `${children.closing}</span>`
          return `<span data-mark="true" class="${clsx(classes)}">${
            children.opening
          }`
        }
        return escapeTextFromHtml(char)
      })

      return htmx.join('')
    }

    const fullHtml = diffs.map((diff) => {
      switch (diff[0]) {
        case DiffMatchPatch.DIFF_EQUAL: {
          const html = getHtmlFromTextWithUnicodeMarks({ text: diff[1] })
          return html
        }
        case DiffMatchPatch.DIFF_INSERT: {
          const html = getHtmlFromTextWithUnicodeMarks({ text: diff[1] })
          const isOpeningMarkSpan = html.startsWith('<span data-mark="true"')
          const isClosingMarkSpan = html === '</span>'
          if (isClosingMarkSpan) return html + '</span>'

          let bgClass = ''
          if (rootNodeStatus !== 'added') {
            if (isConflict) {
              bgClass = 'bg-yellow-diff/20'
            } else if (diffStatus !== 'unmodified') {
              bgClass = 'bg-green-diff/30'
            }
          }

          if (isOpeningMarkSpan) {
            return `<span class="${clsx(bgClass)}">${html}`
          }
          return `<span class="${clsx(bgClass)}">${html}</span>`
        }
        case DiffMatchPatch.DIFF_DELETE: {
          const html = getHtmlFromTextWithUnicodeMarks({ text: diff[1] })
          const isOpeningMarkSpan = html.startsWith('<span data-mark="true"')
          const isClosingMarkSpan = html === '</span>'
          if (isClosingMarkSpan) return html + '</span>'

          let bgClass = ''
          if (rootNodeStatus !== 'removed') {
            if (isConflict) {
              bgClass = 'bg-yellow-diff/20'
            } else if (diffStatus !== 'unmodified') {
              bgClass = 'bg-red-diff/30'
            }
          }

          if (isOpeningMarkSpan) {
            return `<span class="${clsx(bgClass)}">${html}`
          }
          return `<span class="${clsx(bgClass)}">${html}</span>`
        }
      }
      return ''
    })

    const __html = fullHtml.join('')

    return (
      <>
        <span data-span-id={spanId} dangerouslySetInnerHTML={{ __html }} />
        <AttrsTooltip spanId={spanId} __html={__html} />
      </>
    )
  }
)

const SHOW_DELAY = 200
const HIDE_DELAY = 50

const AttrsTooltip = React.memo(
  ({ spanId, __html }: { spanId: string; __html: string }) => {
    const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)
    const [tooltipContent, setTooltipContent] = React.useState(<></>)

    const floating = useFloating({
      placement: 'top',
      open: isTooltipOpen,
      onOpenChange: setIsTooltipOpen,
      middleware: [inline(), flip(), shift(), offset()],
      whileElementsMounted: autoUpdate,
    })

    const hover = useHover(floating.context, {
      delay: {
        open: SHOW_DELAY,
        close: HIDE_DELAY,
      },
      restMs: 40,
    })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover])

    React.useEffect(() => {
      const span = document.querySelector<HTMLSpanElement>(
        `[data-span-id="${spanId}"]`
      )
      if (!span) return

      const handleMouseMove = (e: MouseEvent) => {
        const target = e.target as HTMLElement

        if (span.contains(target)) {
          const relevantElement = target.closest<HTMLElement>(
            'a, span[data-inline-block]'
          )

          if (relevantElement) {
            if (relevantElement.tagName === 'A') {
              setTooltipContent(
                <div>
                  <p className="select-none">URL:</p>
                  <p className="cursor-text">
                    {relevantElement.getAttribute('href')}
                  </p>
                  <p>
                    Open in new tab:{' '}
                    {relevantElement.dataset.openInNewTab === 'true'
                      ? 'Yes'
                      : 'No'}
                  </p>
                </div>
              )
            } else if (relevantElement.dataset.inlineBlock) {
              setTooltipContent(
                <div>
                  <p className="select-none">Linked component:</p>
                  <p className="cursor-text">
                    {relevantElement.getAttribute('id')}
                  </p>
                </div>
              )
            }
            floating.refs.setReference(relevantElement)
            const refProps = getReferenceProps()
            Object.entries(refProps).forEach(([key, value]) => {
              if (typeof value === 'function') {
                relevantElement.addEventListener(
                  key.slice(2).toLowerCase() as keyof HTMLElementEventMap,
                  value as EventListener
                )
              }
            })
          }
        }
      }

      document.addEventListener('mousemove', handleMouseMove)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
      }
    }, [__html, floating.refs, spanId, getReferenceProps])

    if (!isTooltipOpen) return null
    return (
      <FloatingPortal>
        <div
          ref={floating.refs.setFloating}
          {...getFloatingProps()}
          style={{
            ...floating.floatingStyles,
          }}
          className="z-tooltip py-2 px-3 cursor-default bg-dark-shade border border-dark-border-solid text-xs max-w-md rounded-md"
          data-attr-tooltip-for={spanId}
        >
          {tooltipContent}
        </div>
      </FloatingPortal>
    )
  }
)

export const InlineTextDiff = ({
  text,
  textMatch,
  condition,
  className,
  lineBreakNode,
  invertBasedOnCondition,
  isConflictingText,
}: {
  text: string
  textMatch: string
  condition: RichTextBlockInputProps['condition'] | undefined
  className?: string
  lineBreakNode?: string
  invertBasedOnCondition?: boolean
  isConflictingText?: boolean
}) => {
  const diffs = diffText({
    text,
    textMatch,
    condition,
    invertBasedOnCondition,
  })
  return (
    <span className={clsx(className)}>
      {diffs.map((diff, i) => {
        let content = diff[1]
        if (lineBreakNode) {
          content = content.replaceAll('\n', lineBreakNode)
        }
        switch (diff[0]) {
          case DiffMatchPatch.DIFF_INSERT:
            return (
              <span
                key={i}
                className={clsx(
                  isConflictingText ? 'bg-yellow-diff/20' : 'bg-green-diff/30'
                )}
              >
                {content}
              </span>
            )
          case DiffMatchPatch.DIFF_DELETE:
            return (
              <span
                key={i}
                className={clsx(
                  isConflictingText ? 'bg-yellow-diff/20' : 'bg-red-diff/30'
                )}
              >
                {content}
              </span>
            )
          default:
            return <span key={i}>{content}</span>
        }
      })}
    </span>
  )
}

export function diffText({
  text,
  textMatch,
  condition,
  invertBasedOnCondition,
}: {
  text: string
  textMatch: string
  condition: RichTextBlockInputProps['condition'] | undefined
  invertBasedOnCondition?: boolean
}) {
  const diffs = dmp.diff_main(
    invertBasedOnCondition && condition !== 'added' ? text : textMatch,
    invertBasedOnCondition && condition !== 'added' ? textMatch : text
  )
  dmp.diff_cleanupSemantic(diffs)
  return diffs.filter((diff) => {
    switch (diff[0]) {
      case DiffMatchPatch.DIFF_INSERT:
        return condition !== 'removed'
      case DiffMatchPatch.DIFF_DELETE:
        return condition !== 'added'
      default:
        return true
    }
  })
}

function escapeTextFromHtml(text: string) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function isValidOrNull(
  value: Block | null | undefined
): value is RichTextBlock | TextBlock | null {
  return !value || isRichTextBlock(value) || isTextBlock(value)
}

const OverridesDiff: React.FC<
  Omit<RichTextBlockInputProps, 'current' | 'previous'>
> = ({
  change,
  className,
  changesTree,
  condition,
  conflicting,
  status,
  treeOld,
}) => {
  const overrides: {
    overrideKey: string
    current: AtLeast<RichTextBlock, 'id' | 'value' | 'type'>
    previous: AtLeast<RichTextBlock | TextBlock, 'id' | 'value' | 'type'> | null
  }[] = React.useMemo(() => {
    if (!isValidOrNull(change.current)) {
      console.warn(
        'OverridesDiff: rich text overrides diff invalid current change –',
        change
      )
      return []
    }
    return Object.entries(change.current?.variantOverrides ?? {}).map(
      ([key, override]) => {
        invariant(
          change.current?.type === 'rich-text',
          'change.current is required'
        )
        const previousType =
          !change.previous || isRichTextBlock(change.previous)
            ? 'rich-text'
            : 'text'

        const prev = isValidOrNull(change.previous)
          ? change.previous?.variantOverrides?.[key]
          : null
        return {
          overrideKey: key,
          current: {
            id: `${key}-current:${change.current.id.slice(0, 4)}`,
            value: {
              ...change.current.value,
              ...override.value,
            } satisfies RichTextBlock['value'],
            type: 'rich-text',
          },
          previous:
            isValidOrNull(change.previous) && prev?.value
              ? ({
                  id: `${key}-previous:${change.previous?.id.slice(0, 4)}`,
                  value: prev.value,
                  type: previousType,
                } as AtLeast<
                  RichTextBlock | TextBlock,
                  'id' | 'value' | 'type'
                >)
              : null,
        }
      }
    )
  }, [change])

  const Children = React.useCallback(
    (props: (typeof overrides)[number] & { className?: string }) => {
      return (
        <RichTextBlockDiff
          {...props}
          change={{
            id: props.overrideKey + change.id.slice(0, 4),
            status: 'unmodified',
          }}
          changesTree={changesTree}
          condition={condition}
          conflicting={conflicting}
          status={status}
          treeOld={treeOld}
          className={clsx(className, props.className, '!h-auto')}
        />
      )
    },
    [change.id, changesTree, className, condition, conflicting, status, treeOld]
  )

  if (!overrides.length) return null
  return (
    <OverridesDiffWrapper overrides={overrides}>
      {Children}
    </OverridesDiffWrapper>
  )
}
